import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SivaContractCreateDto, SivaPhase, SivaService } from 'src/api/v2';
import { MEMBERS_KEY, SIVA_CONTRACTS_KEY, SIVA_REPORT_DECLARATION } from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';
import { useCluster } from 'src/features/cluster';

export interface ISivaContractsFilterDto {
  year?: number;
  sivaPhases?: SivaPhase[];
  searchString?: string;
}

export function useSivaContracts(queryParams?: ISivaContractsFilterDto) {
  const { cluster } = useCluster();
  const { checkAccess } = useAuth();
  const { write, read } = checkAccess('SivaCore');
  const { t: tCommon } = useTranslation('common');

  const [service] = useState(() => new SivaService());

  const queryClient = useQueryClient();

  const { data, ...rest } = useQuery({
    queryKey: [SIVA_CONTRACTS_KEY, queryParams],
    queryFn: () => service.getSivaContracts(queryParams?.sivaPhases),
    enabled: !!cluster && read === true,
  });

  const createMutation = useMutation({
    mutationFn: (record: SivaContractCreateDto) => {
      if (!write) {
        throw new Error('access denied');
      }

      return service.createSivaContract(record);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([SIVA_CONTRACTS_KEY]);
      queryClient.refetchQueries([SIVA_CONTRACTS_KEY]);
      queryClient.invalidateQueries([MEMBERS_KEY]);
      queryClient.refetchQueries([MEMBERS_KEY]);
      queryClient.invalidateQueries([SIVA_REPORT_DECLARATION]);
    },
  });

  const batchUpdatePhaseMutation = useMutation({
    mutationFn: async ({
      numericIds,
      phase,
      fromDate,
    }: {
      numericIds: number[];
      phase: SivaPhase;
      fromDate: Date;
    }) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      return await service.batchUpdatePhase(numericIds, phase, fromDate);
    },
    onSuccess() {
      queryClient.invalidateQueries([SIVA_CONTRACTS_KEY]);
      queryClient.refetchQueries([SIVA_CONTRACTS_KEY]);
    },
  });

  return {
    contracts: data || [],
    ...rest,

    mutations: {
      create: createMutation,
      batchUpdatePhase: batchUpdatePhaseMutation,
    },
  };
}
